import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  ScreenType,
  useScreenType,
  WMS_MOBILE_MAX_WIDTH,
} from "@redotech/react-util/screen";
import { useTranslation } from "@redotech/redo-i18n";
import type { LocationId } from "@redotech/redo-inventory-id";
import type { ProductId } from "@redotech/redo-product-id";
import type { StorageLocationId } from "@redotech/redo-warehouse-id";
import { toast } from "@redotech/redo-web/alert";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { BinOption, BinSearchDropdown } from "./bin-search-dropdown";
import { useBarcodeScanner } from "./use-barcode-scanner";

export interface AddBinAtLocationTarget {
  locationId: LocationId;
  locationName: string | null;
  /** Bins the product is already in at this location; they can't be picked again. */
  existingBins: ExistingBin[];
}

export interface ExistingBin {
  storageLocationId: StorageLocationId;
  binName: string;
}

/** A product can hold several lots in one bin; list each bin once. */
export function uniqueBins(bins: ExistingBin[]): ExistingBin[] {
  return [...new Map(bins.map((bin) => [bin.storageLocationId, bin])).values()];
}

export const AddBinAtLocationModal = memo(function AddBinAtLocationModal({
  target,
  productId,
  productTitle,
  lotTracked,
  onClose,
  onSuccess,
}: {
  target: AddBinAtLocationTarget | null;
  productId: ProductId;
  productTitle: string;
  lotTracked: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const outboundRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const [selectedBin, setSelectedBin] = useState<BinOption | null>(null);
  const [quantity, setQuantity] = useState<number | undefined>(0);
  const [note, setNote] = useState("");
  const scanSequence = useRef(0);
  const isMobile = useScreenType(WMS_MOBILE_MAX_WIDTH) === ScreenType.MOBILE;

  // Reopening for a different location shouldn't inherit the last entries.
  useEffect(() => {
    scanSequence.current++;
    if (target) {
      setSelectedBin(null);
      setQuantity(0);
      setNote("");
    }
  }, [target]);

  const handleBinScan = useHandler(async (barcode: string) => {
    if (!target) {
      return;
    }
    const sequence = ++scanSequence.current;
    setSelectedBin(null);
    try {
      const { storageLocation } =
        await outboundRpcClient.getStorageLocationByName({
          locationId: target.locationId,
          name: barcode,
        });
      if (sequence !== scanSequence.current) {
        return;
      }
      if (
        !storageLocation ||
        !["bin", "storage", "receiving"].includes(storageLocation.type)
      ) {
        toast(t("Bin {{binName}} not found", { binName: barcode }), {
          variant: "error",
        });
        return;
      }
      if (
        target.existingBins.some(
          (bin) => bin.storageLocationId === storageLocation.id,
        )
      ) {
        toast(t("Already in this bin"), { variant: "error" });
        return;
      }
      setSelectedBin({
        storageLocationId: storageLocation.id,
        binName: storageLocation.name,
      });
    } catch {
      if (sequence === scanSequence.current) {
        toast(t("Bin lookup failed"), { variant: "error" });
      }
    }
  });

  useBarcodeScanner({
    enabled: !!target,
    captureFocus: false,
    onScan: (barcode) => void handleBinScan(barcode),
  });

  const existingStorageLocationIds = useMemo(
    () => target?.existingBins.map((bin) => bin.storageLocationId),
    [target],
  );

  const effectiveQuantity = lotTracked ? 0 : (quantity ?? 0);

  const [saveLoad, triggerSave] = useTriggerLoad(async () => {
    if (!target || !selectedBin) {
      return;
    }
    try {
      await outboundRpcClient.addProductToStorageLocation({
        storageLocationId: selectedBin.storageLocationId,
        productId,
        quantity: effectiveQuantity,
        note: note.trim() || undefined,
      });
      toast(
        effectiveQuantity > 0
          ? `Added ${effectiveQuantity} of ${productTitle} to ${selectedBin.binName}.`
          : `Assigned ${productTitle} to ${selectedBin.binName}.`,
        { variant: "success" },
      );
      onSuccess();
      onClose();
    } catch (error) {
      toast(
        error instanceof Error && error.message
          ? `Failed to add bin: ${error.message}`
          : "Failed to add bin. Please try again.",
        { variant: "error" },
      );
    }
  });

  return (
    <RedoModal
      isOpen={!!target}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Add bin",
        onClick: () => triggerSave(),
        disabled: !selectedBin,
        pending: saveLoad.pending,
        size: "sm",
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose, size: "sm" }}
      subtitle={
        target
          ? target.locationName
            ? `${productTitle} at ${target.locationName}`
            : productTitle
          : undefined
      }
      title="Add bin"
    >
      <Flex dir="column" gap="xl">
        {target && target.existingBins.length > 0 && (
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="medium">
              {t("Current bins")}
            </Text>
            <Flex dir="column" gap="xxs">
              {target.existingBins.map((bin) => (
                <Text fontSize="sm" key={bin.storageLocationId}>
                  {bin.binName}
                </Text>
              ))}
            </Flex>
          </Flex>
        )}
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Bin
          </Text>
          <BinSearchDropdown
            disabledReason="Already in this bin"
            disabledStorageLocationIds={existingStorageLocationIds}
            includeReceivingBins
            key={target?.locationId}
            locationId={target?.locationId}
            onSelect={(bin) => {
              scanSequence.current++;
              setSelectedBin(bin);
            }}
            selectedBin={selectedBin}
          />
        </Flex>

        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Count
          </Text>
          <RedoIncrementDecrement
            disabled={lotTracked}
            min={0}
            setValue={setQuantity}
            size={isMobile ? "regular" : "small"}
            value={lotTracked ? 0 : quantity}
            widthHint="1000"
          />
          <Text fontSize="xs" textColor="secondary">
            {lotTracked
              ? "Lot-tracked products are added with no stock. Receive stock against a lot instead."
              : "Units in this bin. Leave at 0 to assign the product without stock."}
          </Text>
        </Flex>

        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="medium">
            Note (optional)
          </Text>
          <RedoTextInput
            placeholder="Add a note..."
            setValue={setNote}
            size="sm"
            value={note}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
